import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useJobs } from '../hooks/useJobs';
import './LatestJobs.css';

const LatestJobs: React.FC = () => {
  const { language } = useLanguage();
  const { jobs, loading } = useJobs({ limit: 6 });

  // Get the latest 6 jobs sorted by posted date
  const latestJobs = [...jobs]
    .sort((a, b) => new Date(b.posted_date).getTime() - new Date(a.posted_date).getTime())
    .slice(0, 6);

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return language === 'nl' ? 'Vandaag' : 'Today';
    } else if (diffDays === 1) {
      return language === 'nl' ? 'Gisteren' : 'Yesterday';
    } else if (diffDays < 7) {
      return language === 'nl' ? `${diffDays} dagen geleden` : `${diffDays} days ago`;
    } else if (diffDays < 30) {
      const weeks = Math.floor(diffDays / 7);
      return language === 'nl'
        ? `${weeks} ${weeks === 1 ? 'week' : 'weken'} geleden`
        : `${weeks} ${weeks === 1 ? 'week' : 'weeks'} ago`;
    } else {
      const months = Math.floor(diffDays / 30);
      return language === 'nl'
        ? `${months} ${months === 1 ? 'maand' : 'maanden'} geleden`
        : `${months} ${months === 1 ? 'month' : 'months'} ago`;
    }
  };

  return (
    <section className="latest-jobs">
      <div className="jobs-container">
        <div className="jobs-header">
          <h2 className="jobs-title">
            {language === 'nl' ? 'Nieuwste Vacatures' : 'Latest Jobs'}
          </h2>
          <a href="/jobs" className="view-all-jobs">
            {language === 'nl' ? 'Bekijk alle vacatures' : 'View all jobs'} →
          </a>
        </div>

        {loading ? (
          <div className="jobs-loading-state" style={{ textAlign: 'center', padding: '3rem' }}>
            <p>{language === 'nl' ? 'Vacatures laden...' : 'Loading jobs...'}</p>
          </div>
        ) : latestJobs.length === 0 ? (
          <div className="jobs-empty-state" style={{ textAlign: 'center', padding: '3rem' }}>
            <p>{language === 'nl' ? 'Geen vacatures beschikbaar' : 'No jobs available'}</p>
          </div>
        ) : (
          <div className="jobs-grid">
            {latestJobs.map((job) => (
              <a key={job.id} className="job-card" href={`/jobs/${job.id}`} aria-label={language === 'nl' ? job.title_nl : job.title_en}>
                <div className="job-sector-tag">{job.discipline}</div>
                <h3 className="job-title">
                  {language === 'nl' ? job.title_nl : job.title_en}
                </h3>
                <div className="job-meta">
                  <span className="job-location">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
                    </svg>
                    {job.location}
                  </span>
                  <span className="job-type">{job.type}</span>
                  <span className="job-posted">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                    </svg>
                    {formatDate(job.posted_date)}
                  </span>
                </div>
                {job.salary && <div className="job-salary">{job.salary}</div>}
              </a>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default LatestJobs;
